import { Alert, Box, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import {
  boolean,
  check,
  email,
  forward,
  nonEmpty,
  object,
  optional,
  partialCheck,
  pipe,
  string,
  type InferOutput,
} from "valibot";
import { useMutation } from "@tanstack/react-query";
import {
  passwordFields,
  PhoneInput,
  NewPasswordFormFields,
  Controller,
} from "@dmca/frontend/components/input";
import { authClient } from "@dmca/frontend/hooks";
import { valibotResolver } from "@hookform/resolvers/valibot";
import {
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from "@dmca/utils/libphonenumber";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@dmca/frontend/components/button";
import { userPinApi } from "../../user-pin-api";

export const Route = createFileRoute("/_login/create-account")({
  component: RouteComponent,
  validateSearch: object({
    code: optional(string()),
    email: optional(string()),
    token: optional(string()),
  }),
  head: () => ({
    meta: [
      {
        title: "Create Account | DNA Dashboard",
      },
    ],
  }),
});

const createAccountFormData = pipe(
  object({
    email: pipe(string(), email("Invalid email format.")),
    firstName: pipe(string(), nonEmpty("First name is required.")),
    lastName: pipe(string(), nonEmpty("Last name is required.")),
    phoneNumber: pipe(
      object({
        value: string(),
        isVerified: boolean(),
      }),
      check(
        (value) => !value.value || isValidPhoneNumber(value.value),
        "Invalid phone number."
      ),
      check(
        (value) => !value.value || value.isVerified,
        "Phone number must be verified."
      )
    ),
    userPin: pipe(
      string(),
      check(
        (value) => !value || /^[0-9]{5}$/.test(value),
        "Enter exactly five digits."
      )
    ),
    ...passwordFields.entries,
  }),
  forward(
    partialCheck(
      [["password"], ["confirmPassword"]],
      (input) => input.password === input.confirmPassword,
      "Passwords do not match."
    ),
    ["confirmPassword"]
  )
);

type CreateAccountFormFields = InferOutput<typeof createAccountFormData>;

function RouteComponent() {
  const { code = "", email = "", token = "" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<CreateAccountFormFields>({
    resolver: valibotResolver(createAccountFormData),
    defaultValues: {
      email: email || "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      phoneNumber: {
        value: "",
        isVerified: false,
      },
      userPin: "",
    },
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);

  const createAccount = useMutation({
    mutationKey: ["auth", "createAccount"],
    mutationFn: async (data: CreateAccountFormFields) => {
      const resp = await authClient.signUp.email(
        {
          email: data.email,
          password: data.password,
          name: data.firstName + " " + data.lastName,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phoneNumber.value
            ? parsePhoneNumberWithError(data.phoneNumber.value).format("E.164")
            : undefined,
        },
        {
          query: { code },
        }
      );
      if (resp.error) throw resp.error;
      return resp.data;
    },
    onSuccess: async (_, data) => {
      if (data.userPin) {
        try {
          await userPinApi("set", { code: data.userPin });
        } catch (error) {
          setAlert(
            `Account created, but the PIN was not saved: ${
              error instanceof Error
                ? error.message
                : "please add it in Edit Profile."
            }`
          );
          setAccountCreated(true);
          return;
        }
      }
      navigate({ to: "/" });
    },
    onError: (err) => {
      console.error("error", err);
      if (err.message.includes("Invalid signup code")) {
        setAlert("The link you used is invalid or has expired.");
      } else {
        setAlert(err.message);
      }
    },
  });

  const isLoading = createAccount.isPending;
  const handleFormSubmit = (data: CreateAccountFormFields) => {
    createAccount.mutate(data);
  };

  return (
    <Box>
      <Typography sx={{ my: 4, typography: "pageTitle", fontWeight: 700 }}>
        Create a new Daifuku account
      </Typography>
      {alert && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {alert}
        </Alert>
      )}
      {accountCreated && (
        <Button fullWidth href="/settings/user/profile">
          Continue to Edit Profile
        </Button>
      )}
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Controller
          control={control}
          name="email"
          inputProps={{
            label: "Email",
            autoComplete: "email",
            disabled: Boolean(email),
            required: true,
            autoFocus: true,
          }}
        />
        <Controller
          control={control}
          name="firstName"
          inputProps={{
            label: "First Name",
            required: true,
          }}
        />
        <Controller
          control={control}
          name="lastName"
          inputProps={{
            label: "Last Name",
            required: true,
          }}
        />
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field, fieldState }) => (
            <PhoneInput
              {...field}
              fullWidth
              error={!!fieldState.error}
              errorText={fieldState.error?.message}
              label="Phone Number"
              helperText="Optional. Verify this number to use phone notifications."
              invite={{ code, token }}
            />
          )}
        />
        <NewPasswordFormFields control={control} />
        <Controller
          control={control}
          name="userPin"
          inputProps={{
            label: "User PIN (optional)",
            inputMode: "numeric",
            autoComplete: "off",
            slotProps: { htmlInput: { maxLength: 5, pattern: "[0-9]*" } },
          }}
        />

        <Button
          fullWidth
          type="submit"
          loading={isLoading}
          disabled={accountCreated || !isDirty || !isValid}
          sx={{ mt: 2, textTransform: "none", fontSize: "1rem" }}
        >
          Create Account
        </Button>
      </Box>
    </Box>
  );
}
